import { QueryTable } from "@/components/query-table/QueryTable";
import PageHeader from "@/components/PageHeader";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useProjectBreadcrumb } from "../../hooks/useProjectBreadcrumb";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { ProjectScheduleListContent } from "@/modules/projects/components/schedules/ProjectScheduleListContent";
import { ProjectSchedulesContextProvider } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { ReadProjectSchedule } from "@/types/projectSchedule";
import {
  ProjectSchedulesService,
  ProjectSchedulesServiceListQuery,
} from "@/modules/projects/service/projectSchedule";
import { displayProjectSchedule } from "@/schemas/projectSchedule";
import { ProjectSchedulesMenubar } from "@/modules/projects/components/schedules/ProjectSchedulesMenubar";
import { ProjectSchedulesUpdateDialog } from "@/modules/projects/components/schedules/ProjectSchedulesUpdateDialog";
import { DataTableQuery } from "@/types/dataTableQuery";
import { SuccessFetchListResponse } from "@/services/api";
import { ProjectSchedulesAddSchedulesDialog } from "@/modules/projects/components/schedules/ProjectSchedulesAddSchedulesDialog";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useTranslation } from "react-i18next";
import { ProjectSchedulesDeleteDialog } from "./ProjectSchedulesDeleteDialog";
import { DateTime } from "luxon";
import { Module } from "@/constants";
import { formatDate } from "@/lib/utils/formatters";
import { useProjectSchedulesStoresColumns } from "@/modules/projects/hooks/useProjectSchedulesStoresColumns";
import {
  getCurrentYearDateRange,
  getInitialProjectScheduleRange,
} from "@/modules/projects/components/schedules/lib/getInitialProjectScheduleRange";

const ProjectSchedules = () => {
  const name = "projectSchedules";
  const { t } = useTranslation();
  const projectId = useProjectId();
  const options = useCreateDataTableOptions<ReadProjectSchedule>(name);
  const currentYearRange = useMemo(() => getCurrentYearDateRange(), []);
  const [selectedStartDate, setSelectedStartDate] = useState(
    () => currentYearRange.start,
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    () => currentYearRange.end,
  );

  const queryKey = useDynamicQueryParams(name, options, {
    selectedEndDate,
    selectedStartDate,
  });
  const [isUpdateDialogOpened, setIsUpdateDialogOpened] = useState(false);
  const [isAddDialogOpened, setIsAddDialogOpened] = useState(false);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const projectDetailsQuery = useProjectQuery(projectId);
  const [isPending, setIsPending] = useState(false);

  const title = t(`projects.breadcrumb.${name}`);

  const breadcrumbs = useProjectBreadcrumb(projectDetailsQuery, [
    {
      label: title,
    },
  ]);

  const fetchScheduleList = useCallback(
    async (fetchParams: ProjectSchedulesServiceListQuery) => {
      if (selectedStartDate === undefined || selectedEndDate === undefined) {
        throw new Error("Project start date and end date are missing");
      }
      try {
        return await ProjectSchedulesService.getList(projectId, fetchParams, {
          start: selectedStartDate,
          end: selectedEndDate,
        });
      } catch {
        throw new Error(t("projectSchedules.error.scheduleListLoadFailed"));
      }
    },
    [projectId, selectedEndDate, selectedStartDate, t],
  );

  const scheduleListQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchScheduleList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: selectedStartDate !== undefined && selectedEndDate !== undefined,
  });

  const scheduleListQueryContextValue = useMemo<
    DataTableQuery<SuccessFetchListResponse<ReadProjectSchedule>>
  >(
    () => ({
      query: scheduleListQuery,
      isPending,
      setIsPending,
    }),
    [isPending, scheduleListQuery],
  );

  const projectStartDate = useMemo(
    () =>
      projectDetailsQuery.data
        ? DateTime.fromISO(projectDetailsQuery.data.start_date)
            .startOf("day")
            .toJSDate()
        : undefined,
    [projectDetailsQuery.data],
  );

  const projectEndDate = useMemo(
    () =>
      projectDetailsQuery.data
        ? DateTime.fromISO(projectDetailsQuery.data.end_date)
            .endOf("day")
            .toJSDate()
        : undefined,
    [projectDetailsQuery.data],
  );

  useEffect(() => {
    if (
      !(projectStartDate instanceof Date) ||
      !(projectEndDate instanceof Date)
    ) {
      return;
    }
    const { start, end } = getInitialProjectScheduleRange(
      projectStartDate,
      projectEndDate,
    );
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  }, [projectStartDate, projectEndDate]);

  const exportQueryParams = useMemo(() => {
    if (!selectedStartDate || !selectedEndDate) {
      return undefined;
    }
    return {
      project_id: projectId,
      start: formatDate(selectedStartDate),
      end: formatDate(selectedEndDate),
    };
  }, [projectId, selectedStartDate, selectedEndDate]);

  const projectScheduleStoresColumnOverrides =
    useProjectSchedulesStoresColumns();

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} title={title} />
      <ProjectDetailsAndNavigation />
      <QueryTable<ReadProjectSchedule>
        schema={displayProjectSchedule}
        name={name}
        query={scheduleListQuery}
        options={options}
        columnOverrides={projectScheduleStoresColumnOverrides}
        tableFeatures={{
          exportSettings: {
            moduleName: Module.ProjectSchedules,
            queryParams: exportQueryParams,
          },
          initialColumnVisibility: {
            store_ref_number: false,
            store_status: false,
            client_store_number: false,
            project_ref_number: false,
            project_name: false,
            project_status: false,
            project_role_name: false,
            store_coordinator_name: false,
            supervisor_name: false,
            regional_representative_name: false,
            regional_director_name: false,
            settlement_type: false,
            network: false,
            city: false,
            street_name: false,
            store_format: false,
            store_number: false,
          },
        }}
      >
        <ProjectSchedulesMenubar />
        <DataTableQueryContextProvider value={scheduleListQueryContextValue}>
          <RowSelectionProvider>
            <ProjectSchedulesContextProvider
              projectStartDate={projectStartDate}
              projectEndDate={projectEndDate}
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              isUpdateDialogOpened={isUpdateDialogOpened}
              setIsUpdateDialogOpened={setIsUpdateDialogOpened}
              setSelectedStartDate={setSelectedStartDate}
              setSelectedEndDate={setSelectedEndDate}
              isAddDialogOpened={isAddDialogOpened}
              setIsAddDialogOpened={setIsAddDialogOpened}
              isDeleteDialogOpened={isDeleteDialogOpened}
              setIsDeleteDialogOpened={setIsDeleteDialogOpened}
            >
              <ProjectScheduleListContent />
              <ProjectSchedulesUpdateDialog />
              <ProjectSchedulesDeleteDialog />
              <ProjectSchedulesAddSchedulesDialog />
            </ProjectSchedulesContextProvider>
          </RowSelectionProvider>
        </DataTableQueryContextProvider>
      </QueryTable>
    </>
  );
};

export default memo(ProjectSchedules);
